// src/questionnaire/FactorAllocationQuestion.js
import React, { useState } from "react";
import { getFactorIcon } from "./icon-mapping";
import { Tooltip } from "react-tooltip";
import { useTeamContext } from "../TeamContext";
import {Check, X} from "lucide-react";

function FactorAllocationQuestion({
                                      workspace,
                                      questionIndex,
                                      questionTitle,
                                      disclaimer,
                                      suggestedFactors,
                                      answerKey,
                                  }) {
    const { updateWorkspaceData } = useTeamContext();
    const wsP2 = workspace.part2Answers || {};
    const founders = workspace.part1Answers.founderNames || [];
    const currentList = wsP2[answerKey] || [];
    const [newFactorName, setNewFactorName] = useState("");

    const handleAddFactor = (name) => {
        if (currentList.some(f => f.factorName === name)) {
            handleRemoveFactor(name);
            return;
        }
        const newItem = { factorName: name, allocations: {} };
        founders.forEach((fn) => {
            newItem.allocations[fn] = "";
        });
        const updatedList = [...currentList, newItem];
        const newP2 = { ...wsP2, [answerKey]: updatedList };
        updateWorkspaceData(workspace.id, { ...workspace, part2Answers: newP2 });
    };

    function handleRemoveFactor(name) {
        const updated = currentList.filter((x) => x.factorName !== name);
        const newP2 = { ...wsP2, [answerKey]: updated };
        updateWorkspaceData(workspace.id, { ...workspace, part2Answers: newP2 });
    }

    function handleAllocationChange(factorName, founder, val) {
        const updated = currentList.map((item) => {
            if (item.factorName !== factorName) return item;
            return {
                ...item,
                allocations: {
                    ...item.allocations,
                    [founder]: val,
                },
            };
        });
        const newP2 = { ...wsP2, [answerKey]: updated };
        updateWorkspaceData(workspace.id, { ...workspace, part2Answers: newP2 });
    }

    const cardStyle = (isSelected) => ({
        display: "flex",
        alignItems: "center",
        padding: "16px",
        marginBottom: "8px",
        border: `1px solid ${isSelected ? "#ea7a0a" : "#ECECEC"}`,
        borderRadius: "8px",
        backgroundColor: isSelected ? "#f0f7ff" : "#fff",
        cursor: "pointer",
        transition: "all 0.2s ease",
        transform: isSelected ? "scale(1.005)" : "none",
        color: "#28435a"
    });

    return (
        <div style={{ color: "#28435a" }}>
            <h3>Question {questionIndex+1}/15</h3>
            <p>{questionTitle}</p>

            <div style={{
                maxHeight: "400px",
                overflowY: "auto",
                padding: "8px",
                borderRadius: "8px",
                border: "1px solid #ECECEC",
                margin: "16px 0"
            }}>
                {suggestedFactors.map((sf) => {
                    const isSelected = currentList.some(f => f.factorName === sf.name);
                    return (
                        <div
                            key={sf.name}
                            style={cardStyle(isSelected)}
                            onClick={() => handleAddFactor(sf.name)}
                        >
                            <div style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                backgroundColor: isSelected ? "#ea7a0a20" : "#F3F4F6",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "16px"
                            }}>
                                {getFactorIcon(sf.name)}
                            </div>
                            <div style={{ flexGrow: 1 }}>
                                <h4 style={{ margin: 0 }}>{sf.name}</h4>
                                <p style={{ margin: "4px 0 0", fontSize: "0.875rem", color: "#6B7280" }}>
                                    {sf.tooltip}
                                </p>
                            </div>
                            <div style={{
                                width: "24px",
                                height: "24px",
                                borderRadius: "50%",
                                border: `2px solid ${isSelected ? "#ea7a0a" : "#D1D5DB"}`,
                                backgroundColor: isSelected ? "#ea7a0a" : "transparent",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}>
                                {isSelected && <Check size={16} color="#fff" />}
                            </div>
                        </div>
                    );
                })}
            </div>

            <Tooltip id="factor-tooltip" />

            <div style={{ marginBottom: "8px" }}>
                <input
                    type="text"
                    placeholder="Custom factor"
                    value={newFactorName}
                    onChange={(e) => setNewFactorName(e.target.value)}
                    style={{
                        marginRight: "8px",
                        padding: "6px",
                        borderRadius: "4px",
                        border: "1px solid #ccc",
                        color: "#28435a"
                    }}
                />
                <button
                    style={{
                        backgroundColor: "#f7931e",
                        color: "#fff",
                        border: "2px solid #f7931e",
                        padding: "8px 16px",
                        borderRadius: "4px",
                        cursor: "pointer",
                        transition: "background .2s, color .2s, border .2s"
                    }}
                    onClick={() => {
                        const trimmed = newFactorName.trim();
                        if (trimmed) {
                            handleAddFactor(trimmed);
                            setNewFactorName("");
                        }
                    }}
                >
                    + Add
                </button>
            </div>

            {currentList.length === 0 && <p>No factors selected yet.</p>}

            {currentList.length > 0 && (
                <table style={{ width: "100%", borderCollapse: "collapse", marginTop: "16px" }}>
                    <thead>
                    <tr style={{ backgroundColor: "#f2f2f2", color: "#28435a" }}>
                        <th>Factor</th>
                        {founders.map((fn) => (
                            <th key={fn} style={{ textAlign: 'center' }}>{fn}</th>
                        ))}
                        <th style={{ textAlign: 'center' }}>Remove</th>
                    </tr>
                    </thead>
                    <tbody>
                    {currentList.map((item) => (
                        <tr key={item.factorName} style={{ color: "#28435a" }}>
                            <td>{item.factorName}</td>
                            {founders.map((fn) => (
                                <td key={fn} style={{ textAlign: 'center' }}>
                                    <input
                                        type="number"
                                        placeholder="e.g. 2"
                                        style={{
                                            width: "60px",
                                            color: "#28435a",
                                            border: "1px solid #ccc"
                                        }}
                                        value={item.allocations[fn] || ""}
                                        onChange={(e) => handleAllocationChange(item.factorName, fn, e.target.value)}
                                    />
                                    %
                                </td>
                            ))}
                            <td style={{ textAlign: 'center' }}>
                                <button
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: "pointer",
                                        padding: "4px"
                                    }}
                                    onClick={() => handleRemoveFactor(item.factorName)}
                                >
                                    <X size={16} color="#df1c1c" />
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            )}

            {disclaimer && <p style={{ marginTop: "8px", color: "#555" }}>{disclaimer}</p>}
        </div>
    );
}

export default FactorAllocationQuestion;
